import { useCallback, useEffect, useState } from 'react';
import toast from 'react-hot-toast';

/**
 * Shared "Text non-responders" UX state for any page that triggers an SMS
 * nudge to no-response parents. Solves three problems coaches hit:
 *
 *   1. **No confirmation** — clicking instantly fired SMS to parents.
 *      Now a confirm() shows the count and asks before sending.
 *   2. **No loading state** — button looked unresponsive so coaches
 *      clicked repeatedly, queueing duplicate SMS bursts.
 *   3. **No cooldown** — repeated clicks within seconds spammed parents
 *      and burned Twilio budget. Now there's a per-event 5-minute
 *      cooldown persisted in localStorage so a refresh doesn't reset it.
 *
 * Usage:
 *   const { disabled, label, sending, send } = useNudgeCooldown(eventId, count);
 *   <button onClick={send} disabled={disabled}>{label}</button>
 *
 * The `send` function returns a Promise that resolves with the callable's
 * response data so callers can do follow-up work (refreshing local state,
 * etc). It already handles toast.success/error for the user-facing
 * notification — callers shouldn't double-toast.
 */

const COOLDOWN_MS = 5 * 60 * 1000; // 5 minutes
const STORAGE_PREFIX = 'scrimly:nudge:lastSentAt:';

function readLastSent(eventId) {
  if (!eventId || typeof window === 'undefined') return 0;
  try {
    const raw = window.localStorage.getItem(STORAGE_PREFIX + eventId);
    const n = Number(raw);
    return Number.isFinite(n) ? n : 0;
  } catch {
    return 0;
  }
}

function writeLastSent(eventId, ts) {
  if (!eventId || typeof window === 'undefined') return;
  try { window.localStorage.setItem(STORAGE_PREFIX + eventId, String(ts)); } catch {}
}

function fmtMs(ms) {
  const s = Math.max(0, Math.ceil(ms / 1000));
  const m = Math.floor(s / 60);
  const r = s % 60;
  if (m <= 0) return `${r}s`;
  return r === 0 ? `${m}m` : `${m}m ${r}s`;
}

/**
 * @param {string} eventId
 * @param {number} count - number of non-responders (used in confirm copy)
 * @param {(args: {gameId: string}) => Promise<{data: any}>} callable
 *   — the httpsCallable that performs the actual send. Caller owns
 *     constructing it (so this hook stays import-light + testable).
 */
export default function useNudgeCooldown(eventId, count, callable) {
  const [sending, setSending] = useState(false);
  const [now, setNow] = useState(() => Date.now());

  // Re-render every second while the cooldown is active so the countdown
  // label ticks down in real time. Stops once the cooldown expires.
  const lastSent = readLastSent(eventId);
  const remaining = Math.max(0, lastSent + COOLDOWN_MS - now);
  useEffect(() => {
    if (remaining <= 0) return;
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, [remaining]);

  const send = useCallback(async () => {
    if (sending) return null;
    if (remaining > 0) {
      toast(`Wait ${fmtMs(remaining)} before sending another nudge.`);
      return null;
    }
    if (!count || count <= 0) {
      toast('Everyone has already responded.');
      return null;
    }
    const ok = typeof window !== 'undefined' && window.confirm(
      `Send a text reminder to ${count} parent${count === 1 ? '' : 's'} ` +
      `who haven't responded yet?\n\n` +
      `This sends an SMS to each one. There's a 5-minute cooldown ` +
      `before you can nudge again.`
    );
    if (!ok) return null;

    setSending(true);
    try {
      const res = await callable({ gameId: eventId });
      const { sent = 0, attempted = 0, message } = res?.data || {};
      writeLastSent(eventId, Date.now());
      setNow(Date.now()); // trigger cooldown re-render
      if (message) {
        toast(message);
      } else {
        toast.success(
          `Reminder sent to ${sent} of ${attempted} parent${attempted === 1 ? '' : 's'}`
        );
      }
      return res?.data ?? null;
    } catch (err) {
      toast.error(err?.message || 'Failed to send reminder.');
      return null;
    } finally {
      setSending(false);
    }
  }, [eventId, count, sending, remaining, callable]);

  let label;
  if (sending) {
    label = 'On it…';
  } else if (remaining > 0) {
    label = `Sent — wait ${fmtMs(remaining)}`;
  } else {
    label = `Text non-responders${count > 0 ? ` (${count})` : ''}`;
  }

  return {
    sending,
    cooldownRemaining: remaining,
    disabled: sending || remaining > 0 || !count || count <= 0,
    label,
    send,
  };
}
